/*
  NOTE: the documents page has two different component displays for desktop vs mobile.
  If there is a change to the display somewhere, be sure to update both the table and accordion components
*/
import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { GetDocumentsForLoanDocument, GetDocumentUrlDocument } from '@willow/graphql-iso/src/portal';
import { getCdnUrlForFile } from '@willow/shared-iso';
import { DocumentDisplayItem, Pagination, Sentry } from '@willow/shared-web';
import { DocumentId, LoanId } from '@willow/types-iso';

import { useGetImpersonatedUserId } from '../../hooks/useGetImpersonatedUserId';
import { TDocumentTypeOption } from '../../pages/documents/form';
import { getTableItems } from './utils/formatDocuments';
import { ViewDocumentsAccordion } from './ViewDocumentsAccordion/ViewDocumentsAccordion';
import { ViewDocumentsTable } from './ViewDocumentsTable/ViewDocumentsTable';

import './ViewDocuments.scss';

export interface Props {
  documentType: TDocumentTypeOption;
  loanId: LoanId;
}

const NoResultsMessage = {
  current: {
    img: getCdnUrlForFile('statement'),
    title: 'Statement available soon',
    description: 'You will be notified when your statement is ready',
  },
  archived: {
    img: getCdnUrlForFile('archive'),
    title: 'No archived documents',
    description: 'Your loan has no expired documents available',
  },
};

export const ViewDocuments = ({ documentType, loanId }: Props) => {
  const { data } = useQuery(GetDocumentsForLoanDocument, {
    variables: {
      loanId,
      userId: useGetImpersonatedUserId(),
    },
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [getDocumentUrl] = useMutation(GetDocumentUrlDocument);

  const displayDocuments = useMemo(() => {
    return getTableItems(documentType, data?.portalSelf.loan.currentSnapshot.documents ?? []);
  }, [documentType, data]);

  const paginatedDocuments = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;

    return displayDocuments.slice(startIndex, endIndex);
  }, [displayDocuments, currentPage, pageSize]);

  const downloadDocument = useCallback(
    async (documentId: DocumentId) => {
      // Get aws download link for the document clicked
      try {
        const response = await getDocumentUrl({
          variables: { loanId, documentId },
          fetchPolicy: 'no-cache',
        });

        if (!response.data?.getDocumentUrl?.url) {
          Sentry.captureException(`Attempted to download documentId: ${documentId}`);
          toast.error('Something went wrong downloading the document, please try again.');
          return;
        }

        window.open(response.data.getDocumentUrl.url, '_self');
      } catch (err) {
        Sentry.captureException(err);
        toast.error('Something went wrong downloading the document, please try again.');
      }
    },
    [loanId, getDocumentUrl],
  );

  const onDocumentDownloadClick = useCallback(
    (document: DocumentDisplayItem) => downloadDocument(document.id as DocumentId),
    [downloadDocument],
  );

  return (
    <section className="view-documents">
      {displayDocuments?.length === 0 ? (
        // NO DOCUMENTS TO DISPLAY
        <div className="view-documents__empty" data-testid="view-documents-empty">
          <div className="view-documents__empty__icon" aria-hidden="true">
            <img src={NoResultsMessage[documentType].img} alt="No results found illustration" />
          </div>
          <div className="view-documents__empty__content">
            <h2>{NoResultsMessage[documentType].title}</h2>
            <h3>{NoResultsMessage[documentType].description}</h3>
          </div>
        </div>
      ) : (
        // PAGINATED LIST OF DOCUMENTS
        <>
          <ViewDocumentsTable
            documents={paginatedDocuments}
            handleDocumentDownload={(document) => onDocumentDownloadClick(document)}
          />
          <ViewDocumentsAccordion
            documents={paginatedDocuments}
            handleDocumentDownload={(document) => onDocumentDownloadClick(document)}
          />
          <Pagination
            pageIndex={currentPage}
            totalResults={displayDocuments?.length}
            pageSize={pageSize}
            gotoPage={setCurrentPage}
            setPageSize={setPageSize}
          />
        </>
      )}
    </section>
  );
};
